import { useEffect, useState, useRef, useCallback } from 'react';
import { WHATS_NEW, type WhatsNewEntry } from '@/lib/whats-new';

const STORAGE_KEY = 'cp.whats_new_version';

export function WhatsNewModal() {
  const [entry, setEntry] = useState<WhatsNewEntry | null>(null);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    const latest = WHATS_NEW[0];
    if (!latest) return;

    const seen = localStorage.getItem(STORAGE_KEY);
    if (seen !== latest.version) {
      setEntry(latest);
    }
  }, []);

  const dismiss = useCallback(() => {
    if (entry) {
      localStorage.setItem(STORAGE_KEY, entry.version);
    }
    setEntry(null);
  }, [entry]);

  if (!entry) return null;

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6'
      onClick={dismiss}
    >
      <div
        className='bg-white rounded-[22px] w-full max-w-[400px] max-h-[80vh] flex flex-col border border-gray-200 shadow-xl overflow-hidden'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className='pt-7 px-6 pb-2 text-center'>
          <span className='inline-block px-3.5 py-1 rounded-full bg-primary text-white text-[13px] font-bold tracking-wide mb-4'>
            v{entry.version}
          </span>
          <h2
            className='text-2xl font-extrabold text-gray-900 tracking-tight'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {entry.title}
          </h2>
        </div>

        {/* Features */}
        <div className='flex-1 overflow-y-auto px-6 pt-5 pb-2'>
          {entry.features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className='flex items-start gap-3.5 mb-4.5'>
                <div className='w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0'>
                  <Icon className='w-5 h-5 text-primary' />
                </div>
                <div className='pt-0.5'>
                  <p
                    className='text-base font-bold text-gray-900 mb-0.5'
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    }}
                  >
                    {feature.title}
                  </p>
                  <p
                    className='text-sm text-gray-400 leading-5'
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    }}
                  >
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className='px-6 pt-3 pb-6'>
          <button
            onClick={dismiss}
            className='w-full py-3.5 rounded-2xl bg-primary text-white font-bold text-base hover:bg-primary-hover transition-colors'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
